import React from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { Link } from "ds/Button";
import { pause } from "Reducers/environment/actions/pause";
import { resume } from "Reducers/environment/actions/resume";
import { useAppDispatch } from "Store/hooks";

import * as S from "../EnvironmentGeneralSettings.styles";

const BoldBodyText = chunks => <strong>&nbsp;{chunks}</strong>;
export interface PausedEnvSettingsProps {
  isEnvPaused: boolean;
  isEnvActive: boolean;
  isLoading: boolean;
  projectId: string;
  environmentId: string;
  organizationId: string;
}

const PausedEnvSettings = ({
  isEnvPaused,
  isEnvActive,
  isLoading,
  projectId,
  environmentId,
  organizationId
}: PausedEnvSettingsProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  let element: React.ReactNode | null = null;

  if (isLoading) {
    element = <S.Loading data-testid="env_status_loading" />;
  } else {
    const capitalizedStatus = isEnvPaused ? "Paused" : "Active";

    const envStatus = intl.formatMessage(
      {
        id: `environment.settings.paused__or_active_env.title`,
        defaultMessage: `Status is ${capitalizedStatus}`
      },
      {
        strong: BoldBodyText,
        status: capitalizedStatus
      }
    );
    const description = intl.formatMessage({
      id: `environment.settings.${
        isEnvPaused ? "paused" : "active"
      }.description`,
      defaultMessage: isEnvPaused
        ? "The environment was paused after two weeks of inactivity which reduced its carbon consumption."
        : "Each git push to this environment triggers a new deployment."
    });

    element = (
      <div data-testid="env_status_wrapper">
        <S.PausedTitleWrapper weight="semiBold">
          {envStatus}
        </S.PausedTitleWrapper>
        <Typography.BodyText size="medium" weight="regular">
          {description}
          <Link
            analyticId="learnmore"
            href={intl.formatMessage({
              id: isEnvPaused
                ? "links.documentation.paused_environment"
                : "links.documentation.active_environment"
            })}
          >
            &nbsp;
            {intl.formatMessage({
              id: "learnmore",
              defaultMessage: "Learn more"
            })}
          </Link>
        </Typography.BodyText>
        {isEnvPaused ? (
          <S.PausedButton
            data-testid="env_status_resume_btn"
            onClick={() =>
              dispatch(resume({ organizationId, projectId, environmentId }))
            }
          >
            {intl.formatMessage(
              {
                id: `environment.settings.pausedEnv.btn`,
                defaultMessage: `Resume environment`
              },
              {
                status: "Resume"
              }
            )}
          </S.PausedButton>
        ) : isEnvActive ? (
          <S.PausedButton
            data-testid="env_status_paused_btn"
            onClick={() =>
              dispatch(pause({ organizationId, projectId, environmentId }))
            }
          >
            {intl.formatMessage(
              {
                id: `environment.settings.pausedEnv.btn`,
                defaultMessage: `Pause environment`
              },
              {
                status: "Pause"
              }
            )}
          </S.PausedButton>
        ) : (
          // There is a moment when the user pauses/resumes and shows the incorrect button. Therefore, we explicitly ask "isEnvPaused" and "isEnvActive".
          <S.Loading data-testid="env_status_unknown_loading" />
        )}
      </div>
    );
  }
  return <S.PausedWrapper>{element}</S.PausedWrapper>;
};

export default PausedEnvSettings;
